import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody } from "reactstrap";
import crossIcon from "assets/images/cross.svg";
import StaffDefaultImage from "assets/images/staff-default-rounded.png";
import Text from "components/Text";
import Empty from "components/Empty";
import { v4 as uuidv4 } from "uuid";
import styles from "../OfficePolicyManagement.module.scss";

/**
 * Modal to select a single approver for a policy version.
 * Mirrors the SelectApprover from Policy Acknowledgement Template (backup reference).
 * Uses staffList from template approvers (getApproversAsStaffList); no direct import from backup.
 */
export default function SelectPolicyApproverModal({
  isOpen,
  toggle,
  staffList = [],
  selectedApprover,
  onSelect,
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [memberList, setMemberList] = useState([]);

  useEffect(() => {
    if (staffList?.length) setMemberList(staffList);
  }, [staffList]);

  const filteredList = useMemo(() => {
    if (!search?.trim()) return memberList;
    const q = search.replace(/\s+/g, "").toLowerCase();
    return memberList.filter((item) => {
      const full = `${item?.firstName ?? ""}${item?.lastName ?? ""}`.toLowerCase();
      return full.includes(q);
    });
  }, [memberList, search]);

  useEffect(() => {
    if (isOpen) {
      setSearch("");
      setSelectedStaff(selectedApprover ?? null);
    }
  }, [isOpen, selectedApprover]);

  const formatDisplayName = (item) =>
    `${item?.firstName ?? item?.FirstName ?? ""} ${item?.lastName ?? item?.LastName ?? ""}`.trim() || "—";

  const isChecked = (item) => {
    if (!selectedStaff) return false;
    const idMatch = selectedStaff?.id && item?.id && selectedStaff.id === item.id;
    const nameMatch =
      selectedStaff?.firstName &&
      item?.firstName &&
      `${(selectedStaff.firstName || "").trim()} ${(selectedStaff.lastName || "").trim()}`.toLowerCase() ===
        `${(item.firstName || "").trim()} ${(item.lastName || "").trim()}`.toLowerCase();
    return idMatch || nameMatch;
  };

  const handleSelectUser = (staffMember) => {
    setSelectedStaff(staffMember);
  };

  const handleApply = () => {
    if (selectedStaff) {
      onSelect(selectedStaff);
      toggle();
    }
  };

  const handleCancel = () => {
    setSelectedStaff(null);
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleCancel}
      className="modal-dialog-centered modal-width-660 new-group-modal"
      modalClassName="custom-modal"
    >
      <span className="close-btn" onClick={handleCancel}>
        <img src={crossIcon} alt="close" />
      </span>
      <ModalBody>
        <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
          {t("officePolicyManagement.selectAprrover")}
        </Text>

        <div className={styles["select-approver-search-wrap"]}>
          <input
            type="text"
            className="form-control"
            placeholder={t("officePolicyManagement.searchByName")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <ul
          className={styles["select-approver-list"]}
          style={{ maxHeight: "300px", overflowY: "auto" }}
        >
          {filteredList?.length > 0 ? (
            filteredList.map((item) => {
              const checked = isChecked(item);
              return (
                <li key={item?.id ?? item?.userId ?? uuidv4()}>
                  <div
                    className={`${styles["select-approver-list-item"]} ch-radio`}
                  >
                    <label>
                      <input
                        type="radio"
                        name="selectApprover"
                        checked={checked}
                        onChange={() => handleSelectUser(item)}
                      />
                      <span className="d-flex align-items-center">
                        <img
                          src={item?.profilePic || StaffDefaultImage}
                          alt=""
                          className={styles["select-approver-avatar"]}
                        />
                        <div>
                          <div
                            style={{ fontWeight: "600", color: "#102C42" }}
                          >
                            {formatDisplayName(item)}
                          </div>
                          {(item?.title || item?.designation?.name) && (
                            <Text
                              size="12px"
                              weight="400"
                              color="#87928D"
                            >
                              {item?.title || item?.designation?.name || ""}
                            </Text>
                          )}
                        </div>
                      </span>
                    </label>
                  </div>
                </li>
              );
            })
          ) : (
            <li>
              <Empty Message={t("noDataAvaliableForSelectoion")} />
            </li>
          )}
        </ul>

        <button
          type="button"
          className="button button-round button-shadow mr-sm-3 mb-3 w-sm-100 mt-4"
          title={t("notices.select")}
          onClick={handleApply}
          disabled={!selectedStaff}
        >
          {t("notices.select")}
        </button>
        <button
          type="button"
          className="button button-round button-border btn-mobile-link button-dark mb-md-3 mt-md-4"
          onClick={handleCancel}
          title={t("cancel")}
        >
          {t("cancel")}
        </button>
      </ModalBody>
    </Modal>
  );
}
